import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { UserNotification } from "../models/leagueInterfaces";
import {
  getUserNotifications,
  getUserRegistration,
  registerForSeason,
  markNotificationAsRead,
  updateRegistration,
} from "../services/notificationsService";
import { flattenUserNotifications } from "../models/notificationModel";

interface NotificationsStore {
  isBusy: boolean;
  notifications: UserNotification[];
  userRegistrationForSeason?: any ;
  setMarkAsRead: (id: string) => void;
  getAllNotifications: () => void;
  registerForSeason: (userDetails: any) => Promise<any>;
  getUserRegistrationForSeason: (seasonId: string, userEmail: string) => Promise<any>;
}

export const useNotificationsStore = create<NotificationsStore>()(
  devtools(
    (set, get) => ({
      isBusy: false,
      notifications: [],
      userRegistrationForSeason: undefined,
      setMarkAsRead: (id) => {
        set(() => ({ isBusy: true }));
        const notification = get().notifications.find((n) => n.id === id);
        if (!notification) return;
        markNotificationAsRead(notification);
        get().getAllNotifications();
        set(() => ({ isBusy: false }));
      },
      getAllNotifications: async () => {
        try {
          set(() => ({ isBusy: true }));
          const response = await getUserNotifications();
          if (response.code === 6208) {
            const notifications = flattenUserNotifications(response.result);
            set(() => ({ isBusy: false, notifications }));
          } else if (response.code === 6209) {
            set({ isBusy: false, notifications: [] });
          } else {
            set({ isBusy: false });
          }
        } catch (error) {
          console.error("Error fetching notifications:", error);
          set(() => ({ isBusy: false }));
        }
      },

      registerForSeason: async (userDetails: any) => {
        try {
          set(() => ({ isBusy: true }));

          if (userDetails.playerId) {
            const response = await updateRegistration(userDetails);
            if (response.code === 4008) {
              set(() => ({ isBusy: false }));
              return true;
            } else {
              set(() => ({ isBusy: false }));
              return false;
            }
          }

          const response = await registerForSeason(userDetails);
          if (response.code === 4002) {
            set(() => ({ isBusy: false }));
            return true;
          } 

          set(() => ({ isBusy: false }));
          return false;
        } catch (error) {
          console.error(`Error updating user:`, error);
          set(() => ({ isBusy: false }));
        }
      },

      getUserRegistrationForSeason: async (seasonId: string, userEmail: string) => {
        try {
          set(() => ({ isBusy: true }));
          const response = await getUserRegistration(seasonId, userEmail);
          if (response.code === 4014) {
            set(() => ({ isBusy: false, userRegistrationForSeason: response.result }));
            return response;
          } else {
            set(() => ({ isBusy: false }));
          }
        } catch (error) {
          console.error(`Error fetching user registration:`, error);
          set(() => ({ isBusy: false }));
        }
      }
    }),
    {
      name: "notificationsStore",
      enabled: process.env.NODE_ENV === "development",
    }
  )
);
